import { useCallback, useEffect, useState } from 'react';
import { isAudioContextRunning, playCriticalAlert, resumeAudioContext } from '@/utils/audioAlerts';

/** Gap between alarm bursts while an alarm is unacknowledged. */
export const ALARM_REPEAT_MS = 2500;

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

export interface UseAlarmAudioResult {
  /** True once the browser will actually play sound (AudioContext running). */
  audioArmed: boolean;
  /** Arm audio from a user gesture (e.g. an "Enable alarm sound" button). */
  armAudio: () => void;
}

/**
 * Audible alarm loop that respects browser autoplay rules.
 *
 * Browsers refuse to start audio until the user interacts with the page, so a
 * console that has been sitting untouched cannot sound an alarm on its own.
 * This hook therefore ARMS audio on the first pointer/key/touch anywhere on the
 * page (capture phase, so nothing can swallow it), long before any alarm, and
 * reports whether it is armed so the alarm UI can say "sound is blocked" and
 * rely on its visual state instead. It is mounted in the app shell for exactly
 * that reason: the first click on any page arms it.
 *
 * While `active`, a critical burst plays immediately and then every
 * ALARM_REPEAT_MS until `active` goes false (acknowledged).
 */
export function useAlarmAudio(active: boolean): UseAlarmAudioResult {
  const [audioArmed, setAudioArmed] = useState<boolean>(() => isAudioContextRunning());

  const armAudio = useCallback(() => {
    resumeAudioContext()
      .then(() => setAudioArmed(isAudioContextRunning()))
      .catch(() => {
        // Audio unavailable or still blocked; the alarm stays visual.
      });
  }, []);

  // Arm on the first user gesture anywhere on the page.
  useEffect(() => {
    if (audioArmed) return;
    const options: AddEventListenerOptions = { capture: true, passive: true };
    for (const evt of GESTURE_EVENTS) window.addEventListener(evt, armAudio, options);
    return () => {
      for (const evt of GESTURE_EVENTS) window.removeEventListener(evt, armAudio, options);
    };
  }, [audioArmed, armAudio]);

  // Sound the alarm while active and audible.
  useEffect(() => {
    if (!active || !audioArmed) return;
    playCriticalAlert();
    const id = setInterval(playCriticalAlert, ALARM_REPEAT_MS);
    return () => clearInterval(id);
  }, [active, audioArmed]);

  return { audioArmed, armAudio };
}
